'use client';

import dynamic from 'next/dynamic';
import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import useSections from '../../hooks/useSections';
import { setSections } from '../../slices/sections';
import { useAppDispatch } from '../../utils/redux';
import type { HomepageSection, LocationData } from '../../utils/types';
import AppInstallSection from '../blocks/AppInstallSection';
import GearShop from '../blocks/GearShop/GearShop';
import IgnMapsTout from '../blocks/IgnMapsTout/IgnMapsTout';
import ReiTout from '../blocks/ReiTout/ReiTout';
import SeoLinks, { type SeoLinks as SeoLinksData } from '../blocks/SeoLinks';
import SponsoredLists from '../blocks/SponsoredLists';
import type { HitTrail } from '../blocks/TrailCarousel';
import TrailRiverTitle from '../TrailRiverTitle';

const TrailCarousel = dynamic(() => import('../blocks/TrailCarousel'));

type Props = {
  sections?: HomepageSection[];
  latLng?: [number, number];
  locationData?: LocationData;
  isBot?: boolean;
};

const BlockSwitch = ({ sections: initialSections = [], latLng, locationData, isBot = false }: Props) => {
  const languageRegionCode = useLanguageRegionCode();
  const dispatch = useAppDispatch();
  const reduxSections = useSelector((state: { sections?: { sections?: HomepageSection[] } }) => state.sections?.sections);
  const sections = reduxSections || initialSections;

  useEffect(() => {
    if (initialSections.length > 0 && !reduxSections) {
      dispatch(setSections(initialSections));
    }
  }, [initialSections, reduxSections, dispatch]);

  const processedSections = useSections({ initialSections: sections, latLng, locationData });

  const isUsEnglishUser = languageRegionCode === 'en-US' && locationData?.country === 'US';

  // Insert app install section as the second block
  const sectionsWithAppInstall: HomepageSection[] =
    processedSections.length > 0 ? [processedSections[0], { section_type: 'app_install_cta' }, ...processedSections.slice(1)] : processedSections;

  const renderSection = useCallback(
    (section: HomepageSection, idx: number) => {
      switch (section.section_type) {
        case 'seo_links': {
          const seoLinksData = (section.data as SeoLinksData) || [];
          if (seoLinksData.length === 0) {
            return null;
          }
          return <SeoLinks data={seoLinksData} isBot={isBot} showAdditionalLinks={seoLinksData.some(seoSection => seoSection.key === 'POIS')} />;
        }

        case 'sponsored_lists':
          return <SponsoredLists title={section.title} collections={section.collections} />;

        case 'ign_maps_tout_fr':
          return languageRegionCode === 'fr-FR' ? <IgnMapsTout /> : null;

        case 'rei_tout':
          if (isUsEnglishUser) {
            return <ReiTout />;
          }
          return null;

        case 'merchandise_store':
          if (isUsEnglishUser) {
            return <GearShop />;
          }
          return null;

        case 'gear_shop_member_deals_tout':
          if (isUsEnglishUser) {
            return <GearShop tout="memberDeals" />;
          }
          return null;

        case 'trail_list': {
          return (
            <TrailCarousel
              hits={section.hits}
              title={<TrailRiverTitle section={section} idx={idx} locationData={locationData} languageRegionCode={languageRegionCode} />}
            />
          );
        }

        case 'googlebot_trail_list': {
          const hits = section.trails.concat({
            exploreMoreUrl: `/explore/${section.country_slug}`
          } as HitTrail);

          return <TrailCarousel hits={hits} title={section.title || ''} />;
        }

        case 'app_install_cta':
          return <AppInstallSection />;

        default:
          return null;
      }
    },
    [languageRegionCode, locationData, isUsEnglishUser, isBot]
  );

  // keep the same section_type key between renders because GearShop has a signup modal that needs to not be blown away by the re-render
  return sectionsWithAppInstall.map((section, index) => <div key={`${section.section_type}`}>{renderSection(section, index)}</div>);
};

export default BlockSwitch;
